import { AddCircleIcon, GroupsIcon, RocketIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import PageHeading from '@components/PageHeading'
import PageLoader from '@components/PageLoader'
import TripCard from '@components/TripCard'
import { Trip } from '@types'
import React from 'react'
import {
    EmptyState,
    EmptyStateButton,
    EmptyStateButtons,
    EmptyStateContainer,
    EmptyStateIcon,
    EmptyStateText,
    ErrorState,
    HeaderActions,
    HeaderButton,
    TripsGrid,
    TripsListContainer,
    TripsListContent,
    TripsListHeader,
} from './TripsList.styles'

interface TripsListProps {
	trips: Trip[]
	loading: boolean
	error: string | null
	onTripClick: (trip: Trip) => void
	onCreateTrip: () => void
	onJoinTrip: () => void
}

const TripsList: React.FC<TripsListProps> = ({
	trips,
	loading,
	error,
	onTripClick,
	onCreateTrip,
	onJoinTrip,
}) => {
	if (loading) {
		return (
			<TripsListContainer>
				<TripsListContent>
					<PageLoader label="Loading your trips…" />
				</TripsListContent>
			</TripsListContainer>
		)
	}

	if (error) {
		return (
			<TripsListContainer>
				<TripsListContent>
					<ErrorState>
						<strong>Error loading trips:</strong> {error}
					</ErrorState>
				</TripsListContent>
			</TripsListContainer>
		)
	}

	// Show empty state when there are no trips
	if (trips.length === 0) {
		return (
			<TripsListContainer>
				<EmptyStateContainer>
					<EmptyState>
						<EmptyStateIcon>
							<RocketIcon sx={{ fontSize: 128 }} />
						</EmptyStateIcon>
						<EmptyStateText>You aren't in any trips yet. Create or join one below to get started.</EmptyStateText>
						<EmptyStateButtons>
							<EmptyStateButton onClick={onCreateTrip}>
								<AddCircleIcon sx={{ fontSize: 20 }} />
								<span>Create Trip</span>
							</EmptyStateButton>
							<EmptyStateButton onClick={onJoinTrip}>
								<GroupsIcon sx={{ fontSize: 20 }} />
								<span>Join Trip</span>
							</EmptyStateButton>
						</EmptyStateButtons>
					</EmptyState>
				</EmptyStateContainer>
			</TripsListContainer>
		)
	}

	return (
		<TripsListContainer>
			<TripsListContent>
				<TripsListHeader>
					<PageHeading title="My Trips" subtitle={countLabel(trips.length, 'trip')} />
					<HeaderActions>
						<HeaderButton onClick={onCreateTrip}>
							<AddCircleIcon sx={{ fontSize: 20 }} />
							<span>Create Trip</span>
						</HeaderButton>
						<HeaderButton onClick={onJoinTrip}>
							<GroupsIcon sx={{ fontSize: 20 }} />
							<span>Join Trip</span>
						</HeaderButton>
					</HeaderActions>
				</TripsListHeader>

				<TripsGrid>
					{trips.map(trip => (
						<TripCard key={trip.id} trip={trip} onClick={onTripClick} />
					))}
				</TripsGrid>
			</TripsListContent>
		</TripsListContainer>
	)
}

export default TripsList
